import { Translator } from 'Translator';
import { getFilter } from 'data/filters';
import addDays from 'date-fns/addDays';
import validateDateFromStorage from 'flux/utils/validateDateFromStorage';
import Fluxxor from 'fluxxor';
import cloneDeep from 'lodash.clonedeep';
import lscache from 'lscache';
import { Filter, Filters, SearchMode } from 'model/Filters';
import formatDate from 'utils/formatDate';

export const constants = {
  SET_GUESTS: 'FILTER:SET_GUESTS',
  SET_LOCATION: 'FILTER:SET_LOCATION',
  FLUSH_FILTERS: 'FILTER:FLUSH_FILTERS',
  SET_DATES: 'FILTER:SET_DATES',
  SET_AVAILABILITY_FILTER: 'FILTER:SET_AVAILABILITY_FILTER',
  RESET_DATES: 'FILTER:RESET_DATES',
  SET_FILTERS: 'FILTERS:SET_FILTERS',
  SET_LANG: 'FILTERS:SET_LANG',
  SET_SEARCH_MODE: 'FILTERS:SET_SEARCH_MODE',
};

// Keep filters for adults, children, country, city, dates
export const keepEssentialFilters = (
  newFilters: Filters,
  oldFilters: Filters,
): Filters => {
  const clonedOldFilter = cloneDeep(oldFilters);
  const clonedNewFilters = cloneDeep(newFilters);
  const oldAdultsFilter = clonedOldFilter.activeFilters.find(
    (filter) => filter.uid === 'adults',
  );
  const oldChildrenFilter = clonedOldFilter.activeFilters.find(
    (filter) => filter.uid === 'children',
  );
  const oldDateFromFilter = clonedOldFilter.activeFilters.find(
    (filter) => filter.uid === 'date.from',
  );
  const oldDateToFilter = clonedOldFilter.activeFilters.find(
    (filter) => filter.uid === 'date.to',
  );
  const newAdultsFilter = clonedNewFilters.activeFilters.find(
    (filter) => filter.uid === 'adults',
  );
  const newChildrenFilter = clonedNewFilters.activeFilters.find(
    (filter) => filter.uid === 'children',
  );
  const newDateFromFilter = clonedNewFilters.activeFilters.find(
    (filter) => filter.uid === 'date.from',
  );
  const newDateToFilter = clonedNewFilters.activeFilters.find(
    (filter) => filter.uid === 'date.to',
  );

  if (!newAdultsFilter) {
    clonedNewFilters.activeFilters.push(oldAdultsFilter);
  }

  if (!newChildrenFilter) {
    clonedNewFilters.activeFilters.push(oldChildrenFilter);
  }

  if (!newDateFromFilter && oldDateFromFilter) {
    clonedNewFilters.activeFilters.push(oldDateFromFilter);
  }

  if (!newDateToFilter && oldDateToFilter) {
    clonedNewFilters.activeFilters.push(oldDateToFilter);
  }

  return {
    lang:
      clonedNewFilters.lang ||
      clonedNewFilters.lang ||
      Translator.getLanguageCode(),
    country: clonedNewFilters.country,
    city: clonedNewFilters.city,
    page: clonedNewFilters.page,
    activeFilters: [...clonedNewFilters.activeFilters],
  };
};

// changes active filters and keeps all other filters
const changeActiveFilters = (filters: Filters, activeFilters: Filter[]) => {
  activeFilters.forEach((activeFilter) => {
    const index = filters.activeFilters.findIndex(
      (filter) => filter.uid === activeFilter.uid,
    );

    if (index === -1) {
      filters.activeFilters.push(activeFilter);
    } else {
      filters.activeFilters[index] = activeFilter;
    }
  });

  return {
    lang: filters.lang || Translator.getLanguageCode(),
    country: filters.country,
    city: filters.city,
    page: filters.page,
    activeFilters: [...filters.activeFilters],
  };
};

export interface FilterStoreInterface {
  getAdults: () => number;
  getChildren: () => number;
  getFilters: () => Filters;
  resetDates: () => void;
  getSearchMode: () => SearchMode;

  [index: string]: any;
}

export const FiltersStore = Fluxxor.createStore({
  initialize() {
    const adultsFilter = getFilter('adults');
    const childrenFilter = getFilter('children');
    const dateFromFilter = getFilter('date.from');
    const dateToFilter = getFilter('date.to');

    adultsFilter.value = parseInt(lscache.get('adults'), 10) || 4;
    childrenFilter.value = parseInt(lscache.get('children'), 10) || 0;

    this.adults = adultsFilter.value as number;
    this.children = childrenFilter.value as number;

    this.filters = {
      lang: Translator.getLanguageCode(),
      country: null,
      city: null,
      activeFilters: [
        adultsFilter,
        childrenFilter,
        dateFromFilter,
        dateToFilter,
      ],
    };

    this.searchMode = SearchMode.ALL;

    const { dateFrom, dateTo } = validateDateFromStorage(
      lscache.get('date_from'),
      lscache.get('date_to'),
    );

    if (dateFrom) {
      dateFromFilter.value = dateFrom;
      lscache.set('date_from', dateFrom);
    } else {
      lscache.remove('date_from');
    }

    if (dateTo) {
      dateToFilter.value = dateTo;
      lscache.set('date_to', dateTo);
    } else {
      lscache.remove('date_to');
    }

    this.filters = changeActiveFilters(this.filters, [
      dateFromFilter,
      dateToFilter,
    ]);

    this.bindActions(
      constants.SET_GUESTS,
      this.onSetGuests,
      constants.SET_LOCATION,
      this.onSetLocation,
      constants.SET_DATES,
      this.onSetDates,
      constants.SET_AVAILABILITY_FILTER,
      this.onSetAvailabilityFilter,
      constants.FLUSH_FILTERS,
      this.onFlushFilters,
      constants.RESET_DATES,
      this.resetDates,
      constants.SET_FILTERS,
      this.onSetFilters,
      constants.SET_LANG,
      this.setLang,
      constants.SET_SEARCH_MODE,
      this.onSetSearchMode,
    );
  },

  onFlushFilters() {
    const adultsFilter = this.filters.activeFilters.find(
      (filter) => filter.uid === 'adults',
    );
    const childrenFilter = this.filters.activeFilters.find(
      (filter) => filter.uid === 'children',
    );

    this.filters = {
      country: this.filters.country,
      city: this.filters.city,
      activeFilters: [adultsFilter, childrenFilter],
    };
    this.emit('change');
  },

  onSetFilters({ filters }: { filters: Filters }) {
    // cache people and dates
    [
      { uid: 'adults', storageKey: 'adults' },
      { uid: 'children', storageKey: 'children' },
      { uid: 'date.from', storageKey: 'date_from' },
      { uid: 'date.to', storageKey: 'date_to' },
    ].forEach(({ uid, storageKey }) => {
      const activeFilter = this.filters.activeFilters.find(
        (filter) => filter.uid === uid,
      );
      if (activeFilter) {
        lscache.set(storageKey, activeFilter.value);
      } else {
        lscache.remove(storageKey);
      }
    });

    this.filters = filters;
    this.emit('change');
  },

  setLang(lang) {
    if (lang !== this.filters.lang) {
      this.filters.lang = lang;
      this.emit('change');
    }
  },

  onSetGuests({
    nrOfAdults,
    nrOfChildren,
  }: {
    nrOfAdults: number | string;
    nrOfChildren: number | string;
  }) {
    lscache.set('adults', nrOfAdults);
    lscache.set('children', nrOfChildren);
    this.adults = parseInt(nrOfAdults.toString(), 10);
    this.children = parseInt(nrOfChildren.toString(), 10);

    const adultsFilter = getFilter('adults');
    adultsFilter.value = parseInt(nrOfAdults.toString(), 10);
    const childrenFilter = getFilter('children');
    childrenFilter.value = parseInt(nrOfChildren.toString(), 10);

    this.filters = changeActiveFilters(this.filters, [
      adultsFilter,
      childrenFilter,
    ]);
    this.emit('change');
  },

  onSetLocation({ city, country }) {
    const newFilters = cloneDeep(this.filters);
    newFilters.city = city;
    newFilters.country = country;
    this.filters = newFilters;
    this.emit('change');
  },

  onSetAvailabilityFilter({ availability }) {
    const newSelectedFromDate =
      availability === 'tomorrow' ? addDays(new Date(), 1) : new Date();
    const newSelectedFrom = formatDate(newSelectedFromDate, 'yyyy-MM-dd');

    // set from date to relevant availability date
    const fromDateFilter = getFilter('date.from');
    fromDateFilter.value = newSelectedFrom;

    // unset to date
    const toDateFilter = getFilter('date.to');
    toDateFilter.value = null;

    // set availability filter
    const availabilityFilter = getFilter('other.availability.items[]');
    availabilityFilter.subOptions = availabilityFilter.subOptions.filter(
      (subOption) => subOption.value === availability,
    );

    this.filters = changeActiveFilters(this.filters, [
      fromDateFilter,
      toDateFilter,
      availabilityFilter,
    ]);

    this.emit('change');
  },

  onSetDates({ selectedFrom, selectedTo }) {
    const fromDateFilter = getFilter('date.from');
    const toDateFilter = getFilter('date.to');

    const filtersToChange = [];

    // unset availability filter when manually selecting dates
    if (
      this.filters.activeFilters.some(
        (f) => f.uid === 'other.availability.items[]',
      )
    ) {
      const availabilityFilter = getFilter('other.availability.items[]');
      availabilityFilter.subOptions = [];
      filtersToChange.push(availabilityFilter);
    }

    this.selectedFrom = selectedFrom;
    this.selectedTo = selectedTo;
    if (this.selectedFrom) {
      fromDateFilter.value = this.selectedFrom;
      lscache.set('date_from', this.selectedFrom);
    } else {
      fromDateFilter.value = null;
      lscache.remove('date_from');
    }

    if (this.selectedTo) {
      toDateFilter.value = this.selectedTo;
      lscache.set('date_to', this.selectedTo);
    } else {
      toDateFilter.value = null;
      lscache.remove('date_to');
    }

    filtersToChange.push(fromDateFilter);
    filtersToChange.push(toDateFilter);

    this.filters = changeActiveFilters(this.filters, filtersToChange);

    this.emit('change');
  },

  getAdults(): number {
    return this.adults;
  },

  getChildren(): number {
    return this.children;
  },

  getLocation(): { city?: string; country?: string } {
    return {
      city: this.city,
      country: this.country,
    };
  },

  getFilters(): Filters {
    return this.filters;
  },

  resetDates() {
    this.selectedFrom = null;
    this.selectedTo = null;
    const availabilityFilter = getFilter('other.availability.items[]');
    availabilityFilter.subOptions = [];

    this.filters = changeActiveFilters(this.filters, [
      getFilter('date.from'),
      getFilter('date.to'),
      availabilityFilter,
    ]);

    lscache.remove('date_from');
    lscache.remove('date_to');
    this.emit('change');
  },

  getSearchMode() {
    return this.searchMode;
  },

  onSetSearchMode(mode) {
    this.searchMode = mode;
    this.emit('change');
  },
});
